import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { api } from '../lib/api';

export interface Workspace {
  id: string;
  name: string;
  slug: string;
  icon: string | null;
  color: string | null;
  role: 'owner' | 'admin' | 'agent';
}

export interface WorkspaceMember {
  id: string;
  user_id: string;
  full_name: string;
  email: string;
  role: 'owner' | 'admin' | 'agent';
}

const LS_KEY = 'hf-inbox-workspace-id';

export const useWorkspaceStore = defineStore('workspace', () => {
  const workspaces = ref<Workspace[]>([]);
  const currentWorkspaceId = ref<string | null>(localStorage.getItem(LS_KEY));
  const currentRole = ref<'owner' | 'admin' | 'agent' | null>(null);
  const loading = ref(false);

  // ---- Getters ----

  const currentWorkspace = computed<Workspace | null>(() => {
    if (!currentWorkspaceId.value) return null;
    return workspaces.value.find((w) => w.id === currentWorkspaceId.value) ?? null;
  });

  const canManageWorkspace = computed<boolean>(() => {
    return currentRole.value === 'owner' || currentRole.value === 'admin';
  });

  // ---- Actions ----

  async function fetchWorkspaces(): Promise<void> {
    loading.value = true;
    try {
      const res = await api.get('/workspaces');
      workspaces.value = res.data.data as Workspace[];

      // Restore previously selected workspace or default to first
      const stored = localStorage.getItem(LS_KEY);
      const valid = stored && workspaces.value.some((w) => w.id === stored);

      if (valid) {
        currentWorkspaceId.value = stored;
      } else if (workspaces.value.length > 0) {
        currentWorkspaceId.value = workspaces.value[0].id;
        localStorage.setItem(LS_KEY, workspaces.value[0].id);
      } else {
        currentWorkspaceId.value = null;
      }

      // Sync role from the matched workspace entry
      _syncRole();
    } catch (err) {
      console.error('[workspace] fetchWorkspaces failed', err);
    } finally {
      loading.value = false;
    }
  }

  function switchWorkspace(id: string): void {
    currentWorkspaceId.value = id;
    localStorage.setItem(LS_KEY, id);
    _syncRole();
    // Notify other stores that workspace context changed
    window.dispatchEvent(new CustomEvent('workspace:switched', { detail: { id } }));
    // Full reload so all scoped data refetches against the new workspace
    window.location.reload();
  }

  async function createWorkspace(payload: {
    name: string;
    slug?: string;
    icon?: string;
    color?: string;
  }): Promise<Workspace> {
    const res = await api.post('/workspaces', payload);
    const created = res.data.data as Workspace;
    workspaces.value.push(created);
    return created;
  }

  async function updateWorkspace(
    id: string,
    payload: Partial<Pick<Workspace, 'name' | 'slug' | 'icon' | 'color'>>,
  ): Promise<void> {
    const res = await api.patch(`/workspaces/${id}`, payload);
    const updated = res.data.data as Workspace;
    const idx = workspaces.value.findIndex((w) => w.id === id);
    if (idx !== -1) workspaces.value[idx] = { ...workspaces.value[idx], ...updated };
  }

  async function deleteWorkspace(id: string): Promise<void> {
    await api.delete(`/workspaces/${id}`);
    workspaces.value = workspaces.value.filter((w) => w.id !== id);
    if (currentWorkspaceId.value === id) {
      const next = workspaces.value[0] ?? null;
      if (next) {
        switchWorkspace(next.id);
      } else {
        currentWorkspaceId.value = null;
        localStorage.removeItem(LS_KEY);
      }
    }
  }

  // ---- Private helpers ----

  function _syncRole(): void {
    const ws = workspaces.value.find((w) => w.id === currentWorkspaceId.value);
    currentRole.value = ws?.role ?? null;
  }

  return {
    workspaces,
    currentWorkspaceId,
    currentRole,
    loading,
    currentWorkspace,
    canManageWorkspace,
    fetchWorkspaces,
    switchWorkspace,
    createWorkspace,
    updateWorkspace,
    deleteWorkspace,
  };
});
